/** An index that will never produce an item. See `SentenceOrder.skip`. */
const MISSING = Symbol('missing');

/**
 * Releases sentences in index order, across turn boundaries.
 *
 * The server numbers sentences from zero for every utterance, so a queue that
 * only ever counts upward stalls forever on the second turn's sentence 0. The
 * boundary is `TTS_DONE`, but it cannot reset anything on arrival: it is sent
 * once the last sentence has been *scheduled*, while that audio is still
 * playing. So `endTurn` only arms the reset, and the next sentence to arrive
 * performs it.
 *
 * A straggler from the finished turn cannot contaminate the next one, because
 * a websocket delivers in order and the server sends `TTS_DONE` last.
 *
 * Separate from `PlaybackQueue` because this is the whole of the logic and none
 * of the `AudioContext`, which makes it the part that can be tested.
 */
export class SentenceOrder<T> {
  private pending = new Map<number, T | typeof MISSING>();
  private next = 0;
  private newTurn = false;

  accept(sentence: number, item: T): Array<{ index: number; item: T }> {
    return this.deliver(sentence, item);
  }

  /**
   * Give up on a sentence without stranding the ones behind it.
   *
   * Releasing the index is the whole point: sentence `n` is held until `n - 1`
   * has been released, so an index that never arrives is not one silent
   * sentence but every later sentence in the turn. The caller has already
   * decided this one is lost.
   */
  skip(sentence: number): Array<{ index: number; item: T }> {
    return this.deliver(sentence, MISSING);
  }

  private deliver(sentence: number, item: T | typeof MISSING): Array<{ index: number; item: T }> {
    if (this.newTurn) {
      this.newTurn = false;
      this.next = 0;
      this.pending.clear();
    }
    this.pending.set(sentence, item);

    const ready: Array<{ index: number; item: T }> = [];
    for (;;) {
      // `has`, not a truthiness or `undefined` check: a skipped index is
      // present and releases the ones behind it while producing nothing.
      if (!this.pending.has(this.next)) return ready;
      const released = this.pending.get(this.next) as T | typeof MISSING;
      this.pending.delete(this.next);
      const index = this.next;
      this.next += 1;
      if (released !== MISSING) ready.push({ index, item: released });
    }
  }

  endTurn(): void {
    this.newTurn = true;
  }
}
